import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  UserIcon,
  DocumentTextIcon,
  PhotoIcon,
  HeartIcon,
  Squares2X2Icon,
  BookmarkIcon,
  CurrencyDollarIcon,
  GiftIcon
} from '@heroicons/react/24/outline';
import { User, logoutUser } from '../data/mockData';
import { resetCart } from '../utils/cartManager';

interface UserMenuProps {
  user: User;
  onClose: () => void;
  onLogout?: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ user, onClose, onLogout }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const menuItems = [
    { name: 'My Account', icon: UserIcon, href: '/account' },
    { name: 'My Orders', icon: DocumentTextIcon, href: '/orders' },
    { name: 'Saved Designs', icon: BookmarkIcon, href: '/saved-designs' },
    { name: 'My Images', icon: PhotoIcon, href: '/my-images' },
    { name: 'Likes', icon: HeartIcon, href: '/likes' },
    // { name: 'Collections', icon: Squares2X2Icon, href: '/collections' },
    { name: 'Income & Earnings', icon: CurrencyDollarIcon, href: '/income' },
    { name: 'Referral Coupons', icon: GiftIcon, href: '/referrals' },

  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
              <div 
          ref={menuRef}
          className="absolute top-16 right-0 md:right-8 lg:right-12 bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-sm md:w-80"
        >
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
              <UserIcon className="h-6 w-6 text-gray-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                {user.firstName} {user.lastName}
              </h3>
              <p className="text-sm text-gray-500">Member since {user.createdAt ? new Date(user.createdAt).getFullYear() : '2025'}</p>
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <div className="py-2">
          {menuItems.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={onClose}
              className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <item.icon className="h-5 w-5" />
              <span className="text-sm">{item.name}</span>
            </Link>
          ))}
        </div>

        {/* Sign Out */}
        <div className="border-t border-gray-200">
          <button
            onClick={() => {
              logoutUser();
              resetCart(); // Reset cart when user logs out
              onLogout?.();
              onClose();
            }}
            className="w-full flex items-center px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors text-left"
          >
            <span className="text-sm">Sign out</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserMenu; 